import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomSearchInputClassList } from 'component/Search/Input/types';
import OccupancyComponent from 'component/Search/Occupancy';
import { convertValueToOverviewData } from 'component/Search/Occupancy/__helper/convert';

import { useAtom, useSetAtom } from 'jotai';

import { useOccupancy } from 'hooks/form/search';

import { refreshNoteVisibility } from 'data/Atom/Component';
import {
    changedTooltipAtom,
    changedTravellerOrRoomAtom,
} from 'data/Atom/IntegratedOvernight';

import { Occupancy, OccupancyValue } from 'types/Search/form';

interface OccupancyLayerProps {
    showExternalError?: boolean;
    initialValue?: Occupancy;
    placeholder: string;
    label: string;
    customClassList?: CustomSearchInputClassList;
}

const OccupancyLayer = ({
    showExternalError,
    initialValue,
    placeholder,
    label,
    customClassList,
}: OccupancyLayerProps) => {
    const [occupancyValue, setOccupancyValue] = useOccupancy();
    const { t } = useTranslation();
    const [showHighlightData, setHighlightData] = useAtom(
        changedTravellerOrRoomAtom
    );
    const setShowTooltip = useSetAtom(changedTooltipAtom);
    const setIsVisible = useSetAtom(refreshNoteVisibility);

    const onOccupancyChange = useCallback(
        (value: OccupancyValue) => {
            const overview = convertValueToOverviewData(value);
            // update rooms
            setOccupancyValue({
                allocation: value,
                adultCount: overview.adultCount,
                childCount: overview.childAgeList.length,
            });
            setShowTooltip(false);
            setHighlightData(false);
            setIsVisible(true);
        },
        [setOccupancyValue, setShowTooltip, setHighlightData, setIsVisible]
    );
    const highlightComponent = useMemo(
        () =>
            showHighlightData ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: t('TRANS-occupancy-changed-review-the-choice'),
                    }}
                />
            ) : undefined,
        [showHighlightData, t]
    );

    const errorComponent = useMemo(
        () =>
            showExternalError ? (
                <>{t('TRANS-occupancy-changed-review-the-choice')}</>
            ) : undefined,
        [showExternalError, t]
    );

    useEffect(() => {
        if (initialValue) {
            setOccupancyValue(initialValue);
            setIsVisible(false);
        }
    }, [initialValue, setOccupancyValue, setIsVisible]);

    return (
        <OccupancyComponent
            value={occupancyValue.allocation}
            onValueChange={onOccupancyChange}
            errorComponent={errorComponent}
            highlightComponent={highlightComponent}
            placeholder={placeholder}
            label={label}
            customClassList={customClassList}
        />
    );
};

export default OccupancyLayer;
